import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Settings2, ListChecks, Search, Plus, X, ArrowUp, ArrowDown, Boxes } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { DIALOG_TAB_TRIGGER } from '@/components/common/DialogPrimitives';
import { TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { collectionsService, inventoryService } from '@/services/inventory';
import type { Collection } from '@/types/inventory';
import { useDebounced } from '@/hooks/useDebounced';
import { CollectionImageField } from './CollectionImageField';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When supplied, the sheet edits this collection; otherwise it creates one. */
  collection?: Collection | null;
}

interface FormState {
  slug: string;
  name: string;
  description: string;
  status: 'ACTIVE' | 'INACTIVE';
  sortOrder: string;
  startsAt: string;
  endsAt: string;
}

interface PickedItem {
  id: string;
  sku: string;
  name: string;
}

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);

export function CollectionFormSheet({ open, onOpenChange, collection }: Props) {
  const qc = useQueryClient();
  const isEditing = !!collection;

  const [tab, setTab] = useState<'details' | 'products'>('details');
  const [slugTouched, setSlugTouched] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({
    slug: '',
    name: '',
    description: '',
    status: 'ACTIVE',
    sortOrder: '0',
    startsAt: '',
    endsAt: '',
  });

  // ── Products (curated membership) ───────────────────────────────
  const [selected, setSelected] = useState<PickedItem[]>([]);
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);

  const detailQuery = useQuery({
    queryKey: ['inventory', 'collection', collection?.id],
    queryFn: () => collectionsService.get(collection!.id),
    enabled: open && !!collection,
  });

  useEffect(() => {
    if (open) {
      setTab('details');
      setSearch('');
      setForm({
        slug: collection?.slug ?? '',
        name: collection?.name ?? '',
        description: collection?.description ?? '',
        status: collection?.status ?? 'ACTIVE',
        sortOrder: String(collection?.sortOrder ?? 0),
        startsAt: collection?.startsAt ? collection.startsAt.slice(0, 10) : '',
        endsAt: collection?.endsAt ? collection.endsAt.slice(0, 10) : '',
      });
      setSlugTouched(!!collection);
      setImageUrl(collection?.imageUrl ?? null);
      setBannerUrl(collection?.bannerUrl ?? null);
      if (!collection) setSelected([]);
    }
  }, [open, collection]);

  useEffect(() => {
    if (open && detailQuery.data) {
      setSelected(detailQuery.data.items.map((i) => ({ id: i.id, sku: i.sku, name: i.name })));
    }
  }, [open, detailQuery.data]);

  const itemsQuery = useQuery({
    queryKey: ['inventory', 'items', { forCollection: debounced }],
    queryFn: () =>
      inventoryService.list({ search: debounced || undefined, kind: 'FINISHED_GOOD', pageSize: 50 }),
    enabled: open,
  });
  const selectedIds = new Set(selected.map((s) => s.id));
  const results = (itemsQuery.data?.items ?? []).filter(
    (i) => !i.parentItemId && !selectedIds.has(i.id),
  );

  const add = (i: { id: string; sku: string; name: string }) =>
    setSelected((s) => [...s, { id: i.id, sku: i.sku, name: i.name }]);
  const removeAt = (idx: number) => setSelected((s) => s.filter((_, k) => k !== idx));
  const move = (idx: number, dir: -1 | 1) =>
    setSelected((s) => {
      const next = [...s];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return s;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const mutation = useMutation({
    mutationFn: async () => {
      const slug = slugify(form.slug || form.name);
      if (!slug) throw new Error('Enter a name or slug');
      const payload = {
        slug,
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        status: form.status,
        sortOrder: Number(form.sortOrder) || 0,
        startsAt: form.startsAt ? form.startsAt : null,
        endsAt: form.endsAt ? form.endsAt : null,
      };
      const ids = selected.map((s) => s.id);
      if (isEditing && collection) {
        await collectionsService.update(collection.id, payload);
        await collectionsService.setItems(collection.id, ids);
      } else {
        const created = await collectionsService.create(payload);
        if (ids.length) await collectionsService.setItems(created.id, ids);
      }
    },
    onSuccess: () => {
      toast.success(isEditing ? 'Collection saved' : 'Collection created');
      qc.invalidateQueries({ queryKey: ['inventory', 'collections'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'collection', collection?.id] });
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not save collection'),
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? (collection?.name ?? 'Edit collection') : 'New collection'}
      description={
        isEditing
          ? 'Edit this collection’s details and curate its products.'
          : 'A curated marketing group (Gift, Diwali, “Under ₹999”) — a product can be in many. Separate from the product’s category.'
      }
      submitLabel={isEditing ? 'Save changes' : 'Create collection'}
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="4xl"
      fixedHeight="lg"
      tabs={{
        value: tab,
        onValueChange: (v) => setTab(v as 'details' | 'products'),
        triggers: (
          <>
            <TabsTrigger value="details" className={DIALOG_TAB_TRIGGER}>
              <Settings2 className="h-3.5 w-3.5" />
              Details
            </TabsTrigger>
            <TabsTrigger value="products" className={DIALOG_TAB_TRIGGER}>
              <ListChecks className="h-3.5 w-3.5" />
              Products
              <span className="ml-1 text-[11px] text-muted-foreground">{selected.length}</span>
            </TabsTrigger>
          </>
        ),
      }}
    >
      {/* ── Details ─────────────────────────────────────────────── */}
      <TabsContent value="details" className="m-0 space-y-4">
        <FormField label="Name" htmlFor="name" required>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => {
              const name = e.target.value;
              setForm((f) => ({ ...f, name, slug: slugTouched ? f.slug : slugify(name) }));
            }}
            placeholder="Diwali Edit"
            required
            maxLength={200}
          />
        </FormField>

        <FormField
          label="Slug"
          htmlFor="slug"
          required
          hint="URL handle — lowercase, hyphens (e.g. /collections/diwali-edit). Auto-filled from the name."
        >
          <Input
            id="slug"
            value={form.slug}
            onChange={(e) => {
              setSlugTouched(true);
              setForm((f) => ({ ...f, slug: e.target.value.toLowerCase() }));
            }}
            onBlur={() => setForm((f) => ({ ...f, slug: slugify(f.slug) }))}
            placeholder="diwali-edit"
            required
            maxLength={64}
            className="font-mono"
          />
        </FormField>

        <FormField
          label="Status"
          htmlFor="status"
          hint="Inactive collections are hidden from the storefront. Use the schedule for seasonal collections."
        >
          <div className="grid grid-cols-2 gap-2" id="status">
            {(
              [
                { v: 'ACTIVE', title: 'Active', sub: 'Live on the storefront' },
                { v: 'INACTIVE', title: 'Inactive', sub: 'Hidden / draft' },
              ] as const
            ).map((opt) => {
              const active = form.status === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, status: opt.v }))}
                  className={
                    'rounded-md border px-3 py-2 text-left transition-colors ' +
                    (active
                      ? 'border-primary bg-primary/5 ring-1 ring-inset ring-primary/30'
                      : 'bg-card hover:bg-muted/50')
                  }
                >
                  <div className="text-[13px] font-medium text-foreground">{opt.title}</div>
                  <div className="text-[11px] text-muted-foreground">{opt.sub}</div>
                </button>
              );
            })}
          </div>
        </FormField>

        <div className="space-y-2 rounded-md border bg-muted/20 px-3 py-3">
          <div className="text-[12px] font-medium text-foreground">Schedule (optional)</div>
          <p className="text-[11px] text-muted-foreground">
            For seasonal / occasion collections. Leave blank to keep it on whenever it’s Active.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Starts" htmlFor="startsAt">
              <Input
                id="startsAt"
                type="date"
                value={form.startsAt}
                onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))}
              />
            </FormField>
            <FormField label="Ends" htmlFor="endsAt">
              <Input
                id="endsAt"
                type="date"
                value={form.endsAt}
                onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))}
              />
            </FormField>
          </div>
        </div>

        <div className="space-y-3 border-t pt-4">
          <div className="text-[12px] font-medium text-foreground">Images</div>
          {isEditing && collection ? (
            <div className="flex flex-wrap gap-5">
              <div className="space-y-1.5">
                <div className="text-[11.5px] text-muted-foreground">Card image (tile)</div>
                <CollectionImageField
                  collectionId={collection.id}
                  kind="tile"
                  currentUrl={imageUrl}
                  onChanged={(c) => setImageUrl(c.imageUrl ?? null)}
                />
              </div>
              <div className="min-w-[220px] flex-1 space-y-1.5">
                <div className="text-[11.5px] text-muted-foreground">Banner</div>
                <CollectionImageField
                  collectionId={collection.id}
                  kind="banner"
                  currentUrl={bannerUrl}
                  onChanged={(c) => setBannerUrl(c.bannerUrl ?? null)}
                />
              </div>
              <p className="w-full text-[11px] text-muted-foreground">
                Tile — square (≈ 800×800), the storefront <strong>collection card</strong>. Banner
                — 3:1 (≈ 1800×600), the wide <strong>collection-page hero</strong>. Both are served
                to the portal.
              </p>
            </div>
          ) : (
            <div className="rounded-md border border-dashed bg-muted/30 px-3 py-3 text-[12px] text-muted-foreground">
              Save the collection first — then reopen it to upload the tile + banner images.
            </div>
          )}
        </div>

        <FormField label="Description" htmlFor="description">
          <Textarea
            id="description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Optional short description shown on the collection page."
            rows={2}
            maxLength={500}
          />
        </FormField>

        <FormField label="Display order" htmlFor="sortOrder" hint="Lower shows first in lists.">
          <Input
            id="sortOrder"
            type="number"
            value={form.sortOrder}
            onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
            className="w-28"
          />
        </FormField>
      </TabsContent>

      {/* ── Products ────────────────────────────────────────────── */}
      <TabsContent value="products" className="m-0">
        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-2">
            <div className="text-[12px] font-semibold text-foreground">
              In this collection <span className="text-muted-foreground">({selected.length})</span>
            </div>
            <div className="max-h-[46vh] space-y-1.5 overflow-y-auto rounded-md border bg-muted/20 p-2">
              {isEditing && detailQuery.isLoading ? (
                <div className="py-6 text-center text-[12px] text-muted-foreground">Loading…</div>
              ) : selected.length === 0 ? (
                <div className="py-6 text-center text-[12px] text-muted-foreground">
                  No products yet — add some from the right.
                </div>
              ) : (
                selected.map((it, idx) => (
                  <div key={it.id} className="flex items-center gap-2 rounded-md border bg-card px-2.5 py-1.5">
                    <span className="w-5 text-right text-[11px] tabular-nums text-muted-foreground">{idx + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[12.5px] font-medium text-foreground">{it.name}</div>
                      <div className="font-mono text-[10.5px] text-muted-foreground">{it.sku}</div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Button type="button" size="icon" variant="ghost" className="h-6 w-6" disabled={idx === 0} onClick={() => move(idx, -1)} aria-label="Move up">
                        <ArrowUp className="h-3 w-3" />
                      </Button>
                      <Button type="button" size="icon" variant="ghost" className="h-6 w-6" disabled={idx === selected.length - 1} onClick={() => move(idx, 1)} aria-label="Move down">
                        <ArrowDown className="h-3 w-3" />
                      </Button>
                      <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => removeAt(idx)} aria-label="Remove">
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Order here is the storefront display order. Changes save when you click{' '}
              <strong>{isEditing ? 'Save changes' : 'Create collection'}</strong>.
            </p>
          </div>

          <div className="space-y-2">
            <div className="text-[12px] font-semibold text-foreground">Add products</div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search finished goods by SKU or name…"
                className="h-8 pl-8 text-[13px]"
              />
            </div>
            <div className="max-h-[42vh] space-y-1.5 overflow-y-auto rounded-md border bg-muted/20 p-2">
              {itemsQuery.isLoading ? (
                <div className="py-6 text-center text-[12px] text-muted-foreground">Searching…</div>
              ) : results.length === 0 ? (
                <div className="py-6 text-center text-[12px] text-muted-foreground">
                  {debounced ? 'No matching finished goods.' : 'All matching products are already added.'}
                </div>
              ) : (
                results.map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    onClick={() => add(i)}
                    className="flex w-full items-center gap-2 rounded-md border bg-card px-2.5 py-1.5 text-left transition-colors hover:border-primary/40 hover:bg-primary/5"
                  >
                    {i.primaryImageUrl ? (
                      <img src={i.primaryImageUrl} alt="" loading="lazy" className="h-7 w-7 shrink-0 rounded border object-cover" />
                    ) : (
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded border border-dashed bg-muted/40 text-muted-foreground">
                        <Boxes className="h-3.5 w-3.5" />
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[12.5px] font-medium text-foreground">{i.name}</div>
                      <div className="font-mono text-[10.5px] text-muted-foreground">{i.sku}</div>
                    </div>
                    <Plus className="h-3.5 w-3.5 text-primary" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </TabsContent>
    </FormSheet>
  );
}
